/**
 * Where a person was going when they were sent to sign in.
 *
 * A protected screen that finds nobody signed in sends the visitor to
 * `/sign-in?redirect=/practices/p1`, and signing in — by code or through a
 * provider — lands them back on `/es/practices/p1` instead of the dashboard.
 *
 * The value is an **app path without its language**, so the page's language
 * stays the one the visitor is reading when they sign in, even if they
 * switched it on the sign-in screen. It is only ever a path on this origin:
 * anything that could name another host — `//evil.example`, `https:…`, a
 * backslash some browsers read as a slash — is refused, because a sign-in
 * page that can be told where to send someone afterwards is otherwise an open
 * redirect. Nothing secret is ever put in it: it is a path the visitor already
 * had in their address bar.
 */
import { languageFromPathname } from './language.js'

const MAX_LENGTH = 512

/** A safe return path, or null when there is none worth going back to. */
export function safeReturnPath(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_LENGTH) return null
  if (!value.startsWith('/') || value.startsWith('//')) return null
  if (/[\\\s]/.test(value) || /^\/[^/?#]*:/.test(value)) return null
  const path = value.split(/[?#]/)[0]!
  // Back to the sign-in or onboarding screen is nowhere worth going back to,
  // and home is where signing in lands anyway.
  if (path === '/' || path.startsWith('/sign-in') || path.startsWith('/onboarding')) return null
  // A language-prefixed path was built by mistake; the language is the page's.
  if (languageFromPathname(path)) return null
  return value
}

/**
 * The return path for the page being left: its path without the language
 * prefix, with its query string. Hashes are dropped — they are scroll
 * positions, not destinations.
 */
export function returnPathFor(pathname: string, searchStr: string): string | null {
  const withoutLanguage = languageFromPathname(pathname)
    ? pathname.replace(/^\/[^/]+/, '') || '/'
    : pathname
  return safeReturnPath(`${withoutLanguage}${searchStr}`)
}
